import React, { useState } from "react";
import { StaticDateTimePicker } from "@mui/x-date-pickers/StaticDateTimePicker";
import { Box } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { renderMultiSectionDigitalClockTimeView } from "@mui/x-date-pickers";
import CustomActionBar from "./CustomActionBar";
import moment, { Moment } from "moment";
import { useAppSelector } from "hooks/use-app-select";
import { selectScreen } from "redux/appSlice";

const getNext5MinuteInterval = (date: Moment) => {
  const rounded = date.clone();
  const minutes = rounded.minutes();
  const minutesToAdd = minutes % 5 === 0 ? 5 : 5 - (minutes % 5);
  return rounded.add(minutesToAdd, "minutes").seconds(0).milliseconds(0);
};

const MyDateTimePicker = (props: {
  handleAccept: (date: Moment | null) => void;
}) => {
  const [selectedDate, setSelectedDate] = useState<Moment | null>(null);
  const [currentView, setCurrentView] = useState<any>("day");
  const lastClickedElementRef = React.useRef<HTMLElement | null>(null);

  const isMobile = useAppSelector(selectScreen);

  const handlePointerDown = (e: React.MouseEvent | React.TouchEvent) => {
    lastClickedElementRef.current = e.target as HTMLElement;
  };

  const handleViewChange = (newView: any) => {
    const clickedElement = lastClickedElementRef.current;
    const isAmPmItem =
      clickedElement?.closest("li")?.textContent?.trim() === "AM" ||
      clickedElement?.closest("li")?.textContent?.trim() === "PM";

    if (isAmPmItem) {
      return;
    }

    setCurrentView(newView);
  };

  const handleDateChange = (newDate: Moment | null) => {
    if (newDate) {
      const now = moment();
      const next5Min = getNext5MinuteInterval(now);
      if (newDate.isSame(now, "day")) {
        if (newDate.isBefore(next5Min)) {
          newDate.hours(next5Min.hours());
          newDate.minutes(next5Min.minutes());
          newDate.seconds(0);
          newDate.milliseconds(0);
        }
      } else if (newDate.isAfter(now, "day")) {
        if (!selectedDate || selectedDate.isSame(now, "day")) {
          newDate.hours(0);
          newDate.minutes(0);
          newDate.seconds(0);
          newDate.milliseconds(0);
        }
      }
    }
    setSelectedDate(newDate);
  };

  const handleCancel = () => {
    setSelectedDate(null);
  };

  const minDateTime = React.useMemo(() => getNext5MinuteInterval(moment()), []);

  return (
    <Box
      sx={{ width: "100%" }}
      onMouseDown={handlePointerDown}
      onTouchStart={handlePointerDown}
    >
      <StaticDateTimePicker
        orientation={isMobile ? "portrait" : "landscape"}
        value={selectedDate}
        onChange={handleDateChange}
        disablePast={true}
        minDateTime={minDateTime}
        view={currentView}
        onViewChange={handleViewChange}
        slots={{
          actionBar: () => null,
        }}
        viewRenderers={{
          hours: renderMultiSectionDigitalClockTimeView as any,
          minutes: renderMultiSectionDigitalClockTimeView as any,
        }}
        sx={{
          flex: 1,
          width: "100%",
          overflowX: "hidden",
          "& .MuiPickersToolbar-root, & .MuiDateTimePickerToolbar": {
            display: isMobile ? "none" : "flex",
            pointerEvents: "none",
            //
            overflow: "hidden",
            "& .MuiDateTimePickerToolbar-amPmSelection": {
              marginLeft: "4px",
            },
          },
          "& .MuiPickersLayout-root": {
            width: isMobile ? "100%" : "fit-content",
            minWidth: 0,
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            margin: "0 auto",
            //
            overflow: "hidden",
          },
          "& .MuiPickersLayout-contentWrapper": {
            width: isMobile ? "100%" : "auto",
          },
          "& .MuiDateCalendar-root": {
            width: isMobile ? "100%" : "320px",
            maxWidth: isMobile ? "100%" : "320px",
            overflowX: "hidden",
          },
          "& .MuiMultiSectionDigitalClock-root": {
            width: isMobile ? "100%" : "320px",
            maxWidth: isMobile ? "100%" : "320px",
            height: isMobile ? "240px" : "290px",
            maxHeight: isMobile ? "240px" : "290px",
            margin: "0 auto",
            padding: "8px 0",
            paddingBottom: "0px",
            position: "relative",
            "&::after": {
              content: '""',
              display: "block",
              height: "1px",
              backgroundColor: "rgba(71, 85, 105, 0.15)",
              margin: "0 8px",
              position: "sticky",
              bottom: 0,
            },
          },
          "& .MuiMultiSectionDigitalClock-root ul, & .MuiMultiSectionDigitalClock-section, & .MuiMultiSectionDigitalClockSection-root":
            {
              flex: 1,
              //
              minWidth: 0,
              scrollbarWidth: "thin",
              padding: "8px 4px !important",
              paddingBottom: "40px !important",
              scrollPaddingTop: "0 !important",
              scrollPaddingBottom: "0 !important",
              "&::before, &::after": {
                display: "none !important",
                height: "0 !important",
              },
            },
          "& .MuiMultiSectionDigitalClock-root ul:not(:last-child), & .MuiMultiSectionDigitalClock-section:not(:last-child), & .MuiMultiSectionDigitalClockSection-root:not(:last-child)":
            {
              borderRight: "1px solid rgba(71, 85, 105, 0.15)",
            },
          "& .MuiMultiSectionDigitalClock-root .MuiMenuItem-root": {
            justifyContent: "center",
            textAlign: "center",
            margin: "4px 4px",
            borderRadius: "6px",
            transition: "all 0.2s ease",
            padding: "8px 12px",
          },
          "& .MuiMultiSectionDigitalClock-root .MuiMenuItem-root.Mui-selected":
            {
              backgroundColor: (theme) =>
                `${theme.palette.primary.main} !important`,
              color: (theme) => `${theme.palette.common.white} !important`,
              fontWeight: "bold",
            },
          "& .MuiMultiSectionDigitalClock-root .MuiMenuItem-root:hover": {
            backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.08),
          },
          "& .MuiDateTimePickerTabs-root": {
            width: "100%",
            maxWidth: "100%",
            overflow: "hidden",
          },
          "& .MuiTabs-root": {
            minWidth: 0,
            width: "100%",
          },
        }}
        // slotProps={{
        //   layout: {
        //     sx: {
        //       display: "flex",
        //       flexDirection: isMobile ? "column" : "row",
        //       minWidth: 0,
        //       "& .MuiPickersLayout-contentWrapper": {
        //         width: "100%",
        //         overflow: "hidden",
        //       },
        //     },
        //   },
        // }}
        // sx={{
        //   width: "100%",
        //   "& .MuiPickerStaticWrapper-root": {
        //     minWidth: 0,
        //   },
        //   "& .MuiDateCalendar-root": {
        //     width: isMobile ? "100%" : "320px",
        //   },
        // }}
      />
      <CustomActionBar
        onAccept={props.handleAccept}
        selectedDate={selectedDate}
        onClear={() => setSelectedDate(null)}
        // onSetToday={() => handleDateChange(new Date())}
        onCancel={handleCancel}
      />
    </Box>
  );
};

export default MyDateTimePicker;
